"use client";

import Calendar from "@/components/calendar/calendar-view";
import CustomView from "@/components/calendar/custom-view";
import { SELLER_GENERAL_AVAILABILITY_MUTATION } from "@/gql/availability";
import { useAuth } from "@/hooks/use-auth";
import useContainerWidth from "@/hooks/use-container-width";
import { getSellerAvailability } from "@/utils/get-seller-availability";
// import { getSellerAvailability } from "@/utils/get-seller-availability";
import "@/components/calendar/css/calendar.css";
import { ErrorBoundary } from "@/components/error-boundary";
import { logger } from "@/utils/logger";
import { useMutation } from "@apollo/client";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { SlotComponentProps } from "./availabilities";
import { SlotComponent } from "./common/slot-component";
interface SlotTime {
  startTime: Date;
  endTime: Date;
}
const mergeSlots = (slotA: SlotTime[], slotB: SlotTime[]) => {
  const dateConvert = (slot: SlotTime) => new Date(slot.startTime).getTime();
  const allStartSlots = [...slotA, ...slotB].sort((a, b) => dateConvert(a) - dateConvert(b));
  const allStartsSlotsWithoutPast = allStartSlots.filter(slot => new Date(slot.startTime).getTime() > new Date().getTime());
  // This function should merge two slots if they overlap or are adjacent, otherwise return them as is.
  const resultSlots = allStartsSlotsWithoutPast.reduce((acc, slot) => {
    acc[dateConvert(slot)] = slot;
    return acc;
  }, {} as Record<number, SlotTime>);
  return Object.values(resultSlots);
};
function addHours(timeStr: string, hoursToAdd: number) {
  const [time, period] = timeStr.split(" ");
  const date = new Date(`2000/01/01 ${time} ${period}`);
  date.setHours(date.getHours() + hoursToAdd);
  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit"
  });
}
function timeLabelTextFunc(timeLabelText: string) {
  const to = addHours(timeLabelText, 3).toLocaleUpperCase();
  return timeLabelText + " - " + to;
}
export function UnitAvailabilityPage({
  unitId,
  sellerUnitId,
  onSlotSelect,
  selectedSlots: externalSelectedSlots,
  backendFetch = true
  // minSlots = 1,
}: {
  unitId?: string;
  sellerUnitId?: string;
  onSlotSelect?: (slots: SlotTime[]) => void;
  selectedSlots?: SlotTime[];
  backendFetch?: boolean;
  minSlots?: number;
}) {
  const {
    user
  } = useAuth();
  const [sellerAvailability, setSellerAvailability] = useState<{
    currentSellerSlots: SlotTime[];
    otherSellerSlots: SlotTime[];
    buyerSlots: SlotTime[];
  }>({
    currentSellerSlots: [],
    otherSellerSlots: [],
    buyerSlots: []
  });
  // const [selectedSlots, setSelectedSlots] = useState<SlotTime[]>([]);
  // const [isLoading, setIsLoading] = useState(true);
  // const [error, setError] = useState<string | null>(null);
  const [updateSellerAvailability] = useMutation(SELLER_GENERAL_AVAILABILITY_MUTATION);
  const [backendMounted, setBackendMounted] = useState(false);
  const [date, setDate] = useState(new Date());
  // Check if user is a seller
  const sellerGroup = user?.groups?.find(group => group.relationship === "seller");
  const {
    containerWidth,
    containerRef
  } = useContainerWidth();
  const {
    views
  } = useMemo(() => ({
    views: {
      customWeek: CustomView
    }
  }), []);
  const fetchSellerAvailability = useCallback(async (): Promise<SlotTime[]> => {
    try {
      const result = await getSellerAvailability({
        unitId,
        sellerUnitId,
        sellerId: sellerGroup?.id
      });
      if ("error" in result) {
        // setError(result.error.message);
        return [];
      }

      // Convert string dates to Date objects
      const mapSlots = (slots: {
        startTime: string;
        endTime: string;
      }[]) => slots.map(slot => {
        const startTime = new Date(slot.startTime);
        const endTime = new Date(slot.endTime);

        // Validate that the dates are valid
        if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
          return null;
        }
        return {
          startTime,
          endTime
        };
      }).filter((slot_0): slot_0 is {
        startTime: Date;
        endTime: Date;
      } => slot_0 !== null);
      return mapSlots(result.currentSellerSlots);
    } catch (err) {
      logger.error("Error fetching seller availability - unit availability page", {
        error: err
      });
      // setError("Failed to fetch seller availability");
      return [];
    }
  }, [unitId, sellerUnitId, sellerGroup?.id]);
  const handleSlotSelect = useCallback((slotInfo: {
    start: string;
    end: string;
  }) => {
    // Validate the incoming slot info
    const sAvailability = sellerAvailability.currentSellerSlots.map(slot_1 => ({
      startTime: new Date(slot_1.startTime),
      endTime: new Date(slot_1.endTime)
    }));
    const updatedSlots = (() => {
      // Check if slot already exists
      const exists = sAvailability.some(slot_2 => new Date(slot_2.startTime).getTime() === new Date(slotInfo.start).getTime() && new Date(slot_2.endTime).getTime() === new Date(slotInfo.end).getTime());
      if (exists) {
        // Remove if exists
        return sAvailability.filter(slot_3 => new Date(slot_3.startTime).getTime() !== new Date(slotInfo.start).getTime() && new Date(slot_3.endTime).getTime() !== new Date(slotInfo.end).getTime());
      } else {
        // Add if doesn't exist
        return [...sAvailability, {
          startTime: new Date(slotInfo.start),
          endTime: new Date(slotInfo.end)
        }];
      }
    })();
    setSellerAvailability(prev => ({
      ...prev,
      currentSellerSlots: updatedSlots
    }));
    if (onSlotSelect) {
      onSlotSelect(updatedSlots);
    }
  }, [sellerAvailability.currentSellerSlots, onSlotSelect]);
  const handleSubmit = useCallback(async () => {
    if (sellerAvailability.currentSellerSlots.length === 0 && !onSlotSelect) {
      toast.error("Please select at least one time slot");
      return;
    }

    // Validate that all selected slots have valid Date objects
    const validSelectedSlots = sellerAvailability.currentSellerSlots.filter(slot_4 => slot_4.startTime instanceof Date && slot_4.endTime instanceof Date && !isNaN(slot_4.startTime.getTime()) && !isNaN(slot_4.endTime.getTime()));
    if (validSelectedSlots.length === 0 && !onSlotSelect) {
      toast.error("No valid time slots found. Please reselect your availability.");
      return;
    }
    if (!sellerGroup?.id) {
      toast.error("Seller information not found. Please try logging in again.");
      return;
    }
    try {
      const result_0 = await updateSellerAvailability({
        variables: {
          params: {
            sellerGeneralAvailability: {
              ...(unitId && {
                unitId: unitId as string
              }),
              ...(sellerUnitId && {
                sellerUnitId: sellerUnitId as string
              }),
              sellerId: sellerGroup.id,
              slots: validSelectedSlots.map(slot_5 => ({
                startTime: slot_5.startTime.toISOString(),
                endTime: slot_5.endTime.toISOString()
              }))
            }
          }
        }
      });
      if (result_0.data?.seller.sellerGeneralAvailability) {
        // Call external callback if provided
        if (onSlotSelect) {
          onSlotSelect(validSelectedSlots);
        }
        logger.info("Seller availability submitted", {
          slots: validSelectedSlots,
          sellerId: sellerGroup.id,
          unitId
        });
        toast.success("Availability updated successfully!");
        // if(backendFetch)await fetchSellerAvailability();
      } else {
        toast.error(result_0.errors?.[0]?.message || "Failed to update availability");
      }

      // Reset form
      // setSelectedSlots([])
    } catch (err_0) {
      logger.error("Error submitting availability", {
        error: err_0
      });
      toast.error("Failed to update availability");
    }
  }, [sellerAvailability.currentSellerSlots, onSlotSelect, sellerGroup?.id, unitId, sellerUnitId, updateSellerAvailability]);
  useEffect(() => {
    if (!backendFetch && externalSelectedSlots && !backendMounted) {
      setSellerAvailability(prev_0 => ({
        ...prev_0,
        currentSellerSlots: mergeSlots(prev_0.currentSellerSlots, externalSelectedSlots)
      }));
      return () => {
        setBackendMounted(true);
      };
    }
  }, [externalSelectedSlots, backendFetch, backendMounted]);
  useEffect(() => {
    if (backendFetch && sellerGroup?.id && (unitId || sellerUnitId) && !backendMounted) {
      fetchSellerAvailability().then(slots_0 => {
        if (Array.isArray(slots_0) && slots_0.length > 0) {
          setSellerAvailability(prev_1 => ({
            ...prev_1,
            currentSellerSlots: mergeSlots(prev_1.currentSellerSlots, mergeSlots(slots_0, externalSelectedSlots || []))
          }));
        }
      });
      return () => {
        setBackendMounted(true);
      };
    }
  }, [backendFetch, sellerGroup?.id, unitId, sellerUnitId, externalSelectedSlots, backendMounted, fetchSellerAvailability]);
  const visibleDayColumnCounts = containerWidth < 564 ? 2 : containerWidth < 1024 ? 4 : 7;
  const handleNavigate = (action: string) => {
    let newDate = dayjs(date);
    switch (action) {
      case "PREV":
        newDate = newDate.subtract(visibleDayColumnCounts, "day");
        break;
      case "NEXT":
        newDate = newDate.add(visibleDayColumnCounts, "day");
        break;
      case "TODAY":
        newDate = dayjs();
        break;
      default:
        break;
    }
    setDate(newDate.toDate());
  };

  // if (isLoading) {
  // 	return (
  // 		<div className="p-6">
  // 			<div className="max-w-4xl mx-auto">
  // 				<div className="bg-white rounded-lg shadow p-6">
  // 					<div className="flex items-center justify-center">
  // 						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  // 						<span className="ml-2">Loading availability...</span>
  // 					</div>
  // 				</div>
  // 			</div>
  // 		</div>
  // 	);
  // }
  const getSlotComponentProps = React.useCallback((slotValue: Date | string | undefined): SlotComponentProps => {
    if (!slotValue) return {
      selectedCounterSlots: false,
      selectedSlot: false,
      relationship: "buyer",
      slotValue: slotValue ?? ""
    };
    const slotDate = typeof slotValue === "string" ? new Date(slotValue) : slotValue;
    const slotTime = slotDate.getTime();
    return {
      selectedCounterSlots: false,
      selectedSlot: (externalSelectedSlots || []).flat().filter((el: SlotTime) => new Date(el.startTime) > new Date()).map((el_0: SlotTime) => new Date(el_0.startTime).getTime()).includes(slotTime),
      slotValue,
      relationship: "seller"
    };
  }, [externalSelectedSlots]);
  if (!user) {
    return <div className="p-6">
				<div className="max-w-4xl mx-auto">
					<div className="bg-white rounded-lg shadow p-6">
						<div className="text-center text-red-600">
							<h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
							<p>Please log in to manage unit availability.</p>
						</div>
					</div>
				</div>
			</div>;
  }
  return <ErrorBoundary fallback={() => {
    return <div>Error loading calendar</div>;
  }}>
			<div className="p-y-6" id="slotvisit-test1">
				{/* Current Availability Display */}
				{sellerAvailability.currentSellerSlots.length < 0 && <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
						<h3 className="font-medium text-gray-700 mb-2">Current Available Slots</h3>
						<p className="text-gray-600 text-sm">
							No slots are currently available. Use the calendar below to add new availability slots.
						</p>
					</div>}

				{/* Calendar Section */}
				<div className="mb-6">
					<div className="border border-gray-200 rounded-lg p-4" ref={containerRef} id="slotvisit">
						<Calendar CustomSlotComponent={({
            className,
            slotValue: slotValue_0,
            ...rest
          }) => <SlotComponent {...getSlotComponentProps(slotValue_0)} className={className} {...rest} />} key={date.toISOString()} onSelectSlot={(slotInfo_0: {
            start: Date;
            end: Date;
          }) => handleSlotSelect({
            start: new Date(slotInfo_0.start).toISOString(),
            end: new Date(slotInfo_0.end).toISOString()
          })} defaultView="customWeek" defaultDate={new Date(date)} startTime={{
            hour: 9,
            minutes: 0
          }} timeLabelTextFunc={timeLabelTextFunc} endTime={{
            hour: 18,
            minutes: 0
          }} onNavigate={(v: Date) => {
            setDate(v);
          }} toolbarProps={{
            // TitleComp: () => <TitleComp isVisible={sellerAvailability.currentSellerSlots?.length < minSlots} minSlots={minSlots} />,
            viewCounts: visibleDayColumnCounts,
            onNavigate: handleNavigate
          }} selected={sellerAvailability.currentSellerSlots
          // .filter(slot => slot.startTime instanceof Date && slot.endTime instanceof Date)
          .map(slot_6 => ({
            start: new Date(slot_6.startTime).toISOString(),
            end: new Date(slot_6.endTime).toISOString()
          }))} slotDuration={180} slotCount={2} selectable={true} noSelectionOfPastDate={true} viewCounts={visibleDayColumnCounts} views={views as Record<string, React.ComponentType<unknown>>} startWeekDay={new Date(date).getDay()} />
					</div>
				</div>

				{/* Submit Button */}
				{!onSlotSelect && <div className="flex justify-end">
						<button onClick={handleSubmit} disabled={sellerAvailability.currentSellerSlots.length === 0} className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed">
							Update Availability
						</button>
					</div>}
			</div>
		</ErrorBoundary>;
}